import { QuickbuyTireSelector } from "./quickbuy-tireselector";
import userData from "../user-data";
import type { UserData } from "../user-data";
import Mustache from "mustache";
import { initEcomButtons } from '../ecom/ecom';
import toggle from '@nt-workspace/common/build/helpers/toggle';

declare global {
  interface Window {
    ntQuickbuyTranslations: Record<string, string>;
  }
}

export interface QuickBuyProductSize {
  code: string;
  h: number;
  w: number;
  r: number;
  li: number;
  sr: string;
  info: string;
}

export type QuickBuyProduct = {
  /** Product family code. */
  code: string;
  /** Product url. */
  url: string;
  /** Product thumbnail image. */
  image: string;
  /* Product large image URL */
  imageLarge: string | null;
  /** Product marketing name. */
  marketingName: string;
  /** Product description. */
  description: string;
  /** All available tyre sizes. */
  sizes: QuickBuyProductSize[];
}

export default class QuickBuyBar {
  dom: {
    root: HTMLElement,
    tsContainer: HTMLElement,
    ts: HTMLElement,
    productsListContainer: HTMLElement,
    selectedProductContainer: HTMLElement,
    infoContainer: HTMLElement,
    toggler: HTMLButtonElement,
  };

  ts!: QuickbuyTireSelector;

  // The general selected size from tire selector (205/55R16, does not contain t-code level info)
  selectedSize: UserData['selectedSize'] = null;
  // List of products that have selected size
  availableProducts: QuickBuyProduct[] = [];

  productsTemplate!: string;
  selectedProductTemplate!: string;

  translations: Record<string, string> = {};
  lang: string = '';

  constructor(element: HTMLElement) {
    this.lang = document.documentElement.getAttribute('stage-lang') || '';

    this.dom = {
      root: element,
      tsContainer: element.querySelector('[data-quickbuy-ts]') as HTMLElement,
      ts: element.querySelector('[data-quickbuy-tireselector') as HTMLElement,
      productsListContainer: element.querySelector('[data-quickbuy-products]') as HTMLElement,
      selectedProductContainer: element.querySelector('[data-quickbuy-selected-product]') as HTMLElement,
      infoContainer: element.querySelector('[data-quickbuy-info]') as HTMLElement,
      toggler: element.querySelector('[data-quickbuy-toggler]') as HTMLButtonElement,
    };

    this.productsTemplate = (this.dom.root.querySelector('[data-quickbuy-products-template]') as HTMLElement)?.innerHTML;
    this.selectedProductTemplate = (this.dom.root.querySelector('[data-quickbuy-selected-product-template]') as HTMLElement)?.innerHTML;
    this.translations = window.ntQuickbuyTranslations || {};

    // Check that all elements exist
    if(Object.keys(this.dom).every(key => this.dom[key]) && this.productsTemplate && this.selectedProductTemplate) {
      console.log('[QuickBuyBar] init()');

      this.dom.root.classList.add('visible');

      // Init embedded tire selector
      this.ts = new QuickbuyTireSelector(this.dom.ts);

      // Subscribe to user data changes
      userData.subscribe('selectedSize', this.onUserDataSizeChange.bind(this));
      userData.subscribe('selectedProduct', this.onUserDataProductChange.bind(this));
      userData.subscribe('any', this.updateBarContent.bind(this));

      // Check userdata on init
      this.onUserDataSizeChange(userData.get('selectedSize'))
        .then(() => {
          this.onUserDataProductChange(userData.get('selectedProduct'));
        });
      this.updateBarContent(userData.get('any'));

      // These events are delegated because elements are rendered dynamically

      // Change views
      this.dom.productsListContainer.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;

        const changeSizeBtn = target.closest<HTMLElement>('[data-quickbuy-change-size]');
        if(changeSizeBtn) {
          this.setActiveView('tireselector');
          return;
        }
      });

      this.dom.productsListContainer.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;

        // If target is product select
        const productSelect = target.closest<HTMLElement>('[data-quickbuy-product-select]') as HTMLSelectElement;
        if(productSelect) {
          const productCode = target.value;
          if(productCode) {
            this.onProductSelect(productCode);
          }
        }

        // If target is product radio input
        const productRadio = target.closest<HTMLElement>('input[name="quickbuy-product"]') as HTMLInputElement;
        if(productRadio) {
          const productCode = target.value;
          if(productCode) {
            this.onProductSelect(productCode);
          }
        }
      });

      this.dom.selectedProductContainer.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;

        if(target.closest<HTMLSelectElement>('[data-quickbuy-product-size-select]')) {
          const tCode = target.value;

          if(tCode) {
            this.onProductSizeSelect(tCode);
          }
          this.showTcode(tCode);
        }
      });

      // Change views
      this.dom.selectedProductContainer.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;

        if(target.closest<HTMLButtonElement>('[data-quickbuy-change-product]')) {
          this.renderSelectedProduct(null);
          this.setActiveView('products');
          return;
        } else if(target.closest<HTMLButtonElement>('[data-quickbuy-change-size]')) {
          this.renderSelectedProduct(null);
          this.setActiveView('tireselector');
          return;
        }
      });

      // Analytics events
      this.dom.root.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;

        if(target.closest<HTMLButtonElement>('[data-quickbuy-change-product]')) {
          this.sendAnalyticsEvent('quickBuyChangeProduct', {
            tyreSize: userData.get('selectedSize')?.size || '',
            productCode: userData.get('selectedProduct')?.selectedSize?.code || '',
            productFamily: userData.get('selectedProduct')?.code || '',
            productFamilyCode: userData.get('selectedProduct')?.name || '',
          });
        } else if(target.closest<HTMLButtonElement>('[data-quickbuy-change-size]')) {
          this.sendAnalyticsEvent('quickBuyChangeSize', {
            tyreSize: userData.get('selectedSize')?.size || '',
            productCode: userData.get('selectedProduct')?.selectedSize?.code || '',
            productFamily: userData.get('selectedProduct')?.code || '',
            productFamilyCode: userData.get('selectedProduct')?.name || '',
          });
          return;
        } else if(target.closest<HTMLElement>('[data-gtm-button]')) {
          this.handleAnalyticsButton(target.closest<HTMLElement>('[data-gtm-button]'), e);
          return;
        }
      });

      // Track analytics when quickbuy popover is toggled
      this.dom.toggler.addEventListener('toggle:change', ((e: CustomEvent) => {
        this.sendAnalyticsEvent(e.detail.isOpen ? 'quickBuyOpen' : 'quickBuyClose');
      }) as EventListener);

      // Toggle quick buy popover open from CTA buttons in other elements
      const toggles = document.querySelectorAll<HTMLElement>('.js-open-quickbuy');
      toggles.forEach(toggleEl => {
        toggleEl.addEventListener('click', (e) => {
          toggle.toggle(this.dom.toggler, true);
        });
      });

    } else {
      console.error('[QuickBuyBar]: missing required elements or templates', this);
    }
  }

  handleAnalyticsButton(btn: HTMLElement | null, event: Event) {
    if(!btn) {
      return;
    }

    const eventType = btn.dataset.gtmButton;
    const itemCode = btn.closest<HTMLElement>('[data-gtm-item-code]')?.dataset.gtmItemCode || '';
    const itemSize = btn.closest<HTMLElement>('[data-gtm-item-size]')?.dataset.gtmItemSize || '';
    const familyCode = btn.closest<HTMLElement>('[data-gtm-family-code]')?.dataset.gtmFamilyCode || '';
    const familyName = btn.closest<HTMLElement>('[data-gtm-family-name]')?.dataset.gtmFamilyName || '';

    if(eventType) {

      this.sendAnalyticsEvent(eventType, {
        tyreSize: itemSize,
        productCode: itemCode,
        productFamily: familyName,
        productFamilyCode: familyCode,
      });

      // Prevent default action so events have a bit of time to get sent
      const isLink = btn.tagName === 'A' && (btn.getAttribute('href') || '').indexOf('#') !== 0;
      if (isLink) {
        event.preventDefault();

        setTimeout(() => {
          const href = btn.getAttribute('href');
          if (href !== null) window.location.href = href;
        }, 500);
      }
    }
  }

  sendAnalyticsEvent(eventType: string, additionalData: Record<string, any> = {}) {
    const analyticsData = {
      event: eventType,
      ...additionalData,
    };

    console.log('[QuickBuyBar] sendAnalyticsEvent():', analyticsData);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(analyticsData);
  }

  setLoading(isLoading: boolean) {
    this.dom.root.classList.toggle('loading', isLoading);
  }

  updateBarContent(data: UserData) {
    // console.log('[QuickBuyBar] updateBarContent(): user data changed:', data);
    // Order of data to show: t-code, product name, vehicle, vehicleNA, license, selectedSize
    let markup:string|null = null;

    if(data.selectedProduct) {
      markup = `<strong>${data.selectedProduct.name}</strong>`;
      if(data.selectedProduct.selectedSize) {
        markup += `<br>${data.selectedProduct.selectedSize.info} (${data.selectedProduct.selectedSize.code})`;
      }
    } else if(data.vehicle) {
      markup = `${data.vehicle.manufacturerName} ${data.vehicle.modelName}`;
      if(data.selectedSize) {
        markup += `<br>${data.selectedSize.size}`;
      }
    } else if(data.vehicleNA) {
      markup = `${data.vehicleNA.manufacturer} ${data.vehicleNA.model}`;
      if(data.selectedSize) {
        markup += `<br>${data.selectedSize.size}`;
      }
    } else if(data?.license?.vehicleData?.modelData) {
      markup = `${data.license.vehicleData.modelData.manufacturerName} ${data.license.vehicleData.modelData.modelName}`;
      if(data.selectedSize) {
        markup += `<br>${data.selectedSize.size}`;
      }
    } else if(data.selectedSize) {
      markup = `${this.translations.selected_size || 'Selected size'}<br>${data.selectedSize.size}`;
    }

    // Change button text
    const btnText = this.dom.toggler.querySelector('[data-quickbuy-toggler-text]') as HTMLElement;
    if(btnText) {
      if(data.selectedProduct) {
        btnText.innerText = this.translations.buy_tires || 'Buy tires';
      } else if(data.selectedSize) {
        btnText.innerText = this.translations.change || 'Change';
      } else {
        btnText.innerText = this.translations.find_tires || 'Find tires';
      }
    }


    // Change button type if product selected
    if(data.selectedProduct) {
      this.dom.toggler.classList.remove('button--ghost');
      this.dom.toggler.classList.add('button--primary');
    } else {
      this.dom.toggler.classList.add('button--ghost');
      this.dom.toggler.classList.remove('button--primary');
    }

    if(markup) {
      this.dom.infoContainer.innerHTML = markup;
    } else {
      this.dom.infoContainer.innerHTML = '';
    }
  }

  setActiveView(viewName: 'tireselector' | 'products' | 'selectedProduct') {
    this.dom.tsContainer.classList.toggle('hidden', viewName !== 'tireselector');
    this.dom.productsListContainer.classList.toggle('hidden', viewName !== 'products');
    this.dom.selectedProductContainer.classList.toggle('hidden', viewName !== 'selectedProduct');
  }

  renderProductList() {
    this.dom.productsListContainer.innerHTML = Mustache.render(this.productsTemplate, { products: this.availableProducts, selectedSize: this.selectedSize });
  }

  clearProductList() {
    this.availableProducts = [];
    this.dom.productsListContainer.innerHTML = '';
  }

  renderSelectedProduct(productData: UserData['selectedProduct']) {
    // console.log('[QuickBuyBar] renderSelectedProduct():', productData);

    if(!productData) {
      this.dom.selectedProductContainer.innerHTML = '';
    } else {
      // Add selected boolean to size for mustache
      const templateData = {
        ...productData,
        sizes: productData.sizes.map(size => ({
          ...size,
          selected: productData.selectedSize?.code === size.code,
        })),
      };

      this.dom.selectedProductContainer.innerHTML = Mustache.render(this.selectedProductTemplate, { product: templateData, selectedSize: this.selectedSize });

      // Register ecom links
      const ecomPurchaseButtons = this.dom.selectedProductContainer.querySelectorAll('[data-ecom-product-code]');
      if (ecomPurchaseButtons.length > 0 && window.ecomEnabled === '1') {
        initEcomButtons(ecomPurchaseButtons as NodeListOf<HTMLButtonElement>);
      }
    }
  }

  showTcode(tCode: string) {
    const sizeActions = this.dom.selectedProductContainer.querySelectorAll<HTMLElement>('[data-quickbuy-product-size]');
    sizeActions.forEach(actionEl => {
      actionEl.hidden = actionEl.dataset.quickbuyProductSize !== tCode;
    });
  }

  /**
   * Listens to changes in user data. Changes can come from anywhere.
   */
  onUserDataProductChange(data: UserData['selectedProduct']) {
    this.renderSelectedProduct(data);
    const hasSizeData = userData.get('selectedSize') !== null;
    if(hasSizeData && data) {
      // Has size and product, show selected product view
      this.setActiveView('selectedProduct');
    } else if(hasSizeData && !data) {
      // Has size but no product, show product list
      this.setActiveView('products');
    } else {
      // No size, show tire selector
      this.setActiveView('tireselector');
    }
  }


  /**
   * Product (family level) selected in quick buy popover.
   */
  onProductSelect(productCode: string) {
    // console.log('[QuickBuyBar] onProductSelect(): product selected:', productCode);

    const product = this.availableProducts.find(p => (p as any).code === productCode);
    if(!product) {
      console.error('[QuickBuyBar] onProductSelect(): selected product not found', productCode, this.availableProducts);
      return;
    }

    // Store user data which triggers ui update in quick buy
    userData.store({
      selectedProduct: {
        code: product.code,
        name: product.marketingName,
        image: product.image,
        imageLarge: product.imageLarge,
        url: product.url,
        sizes: product.sizes.map(size => ({
          code: size.code,
          info: size.info,
        })),
        // Select first size by default, user can change it in selected product view
        selectedSize: product.sizes[0] ? {
          code: product.sizes[0].code,
          info: product.sizes[0].info,
        } : null,
      },
    });
  }

  /**
   * Product size (t-code level) selected in the quick buy popover.
   */
  onProductSizeSelect(tCode: string) {
    const currentProductData = userData.get('selectedProduct');
    if(!currentProductData) {
      console.error('[QuickBuyBar] onProductSizeSelect(): no product selected', tCode);
      return;
    }

    const selectedSize = currentProductData.sizes.find(size => size.code === tCode);
    if(!selectedSize) {
      console.error('[QuickBuyBar] onProductSizeSelect(): selected size not found in current product sizes', tCode, currentProductData.sizes);
      return;
    }

    // Store user data which triggers ui update in quick buy
    userData.store({
      selectedProduct: {
        ...currentProductData,
        selectedSize: {
          code: selectedSize.code,
          info: selectedSize.info,
        },
      },
    });
  }

  async onUserDataSizeChange(sizeObj: UserData['selectedSize']) {
    // If size has not changed, set next view but do not fetch products again
    if(sizeObj && this.selectedSize) {
      if(sizeObj.size === this.selectedSize.size) {
        this.setActiveView('products');
        return;
      }
    }

    console.log('[QuickBuyBar] onSizeSelect(): size selected:', sizeObj);
    this.selectedSize = sizeObj;

    if(sizeObj === null) {
      this.clearProductList();
      this.renderSelectedProduct(null);
      this.setActiveView('tireselector');
      return;
    }

    this.setLoading(true);

    // Fetch products that have selected size
    try {
      const sizeSlug = `${sizeObj.width}-${sizeObj.height}-${sizeObj.rim}`;
      const products = await this.fetchProductsForSize(sizeSlug);
      this.availableProducts = products;
    } catch (error) {
      this.availableProducts = [];
      console.error('[QuickBuyBar] onSizeSelect(): error fetching products for size', error);
    } finally {
      this.renderProductList();
      this.setActiveView('products');
      this.setLoading(false);
    }
  }

  /**
   * Fetches suitable products for selected size from server.
   *
   * @param sizeSlug - Size slug in the format "width-height-rim"
   * @returns Found products or empty array if no products are available for the selected size.
   */
  async fetchProductsForSize(sizeSlug: string): Promise<QuickBuyProduct[]> {
    const response = await fetch(`/api/v1/products/by-size/${encodeURIComponent(sizeSlug)}/?stage-language=${this.lang}`, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'xhr',
      },
    });
    const json = await response.json();
    const products = json?.result?.data || [];
    return products;
  }
}
